import type { ContentCollectionItem } from '@nuxt/content'
import type { UseHeadInput, UseSeoMetaInput } from '@unhead/vue'
import { PageSchema, type PageMatter } from '~/shared/utils/defaultDataSchema'
import { defaults } from '~/shared/utils/defaultDataSchema'
import { useRoute } from 'vue-router'

type UseHeadAndSeoInput = {
  headData: UseHeadInput
  seoMetaData: UseSeoMetaInput
  pageData: PageMatter
}

export const setSEO = (
  pageRaw: ContentCollectionItem | Record<string, unknown>, // Accept either a ContentCollectionItem or a plain object for page frontmatter data
  routePath: string
): UseHeadAndSeoInput => {

  let pageData: PageMatter  = {} as PageMatter;

  const route = useRoute();

  try {
    if(!(pageRaw && pageRaw.title)) {
      console.warn('Page frontmatter is missing required "title" field. Using defaults for SEO metadata.', {
        pageRaw,
        routePath,
        route: route.fullPath,
      });
    }
    pageData = PageSchema.parse(pageRaw);

  } catch (error) {
    const safeError = error instanceof Error
      ? {
          name: error.name,
          message: error.message,
          stack: error.stack,
        }
      : {
          message: String(error),
        }

    console.error('Error parsing page frontmatter with schema:', safeError);
    pageData = pageData || {} as PageMatter; // Ensure pageData is at least an empty object if parsing fails
  }

  const keywords = Array.isArray(pageData.keywords)
    ? defaults.keywords.concat(pageData.keywords)
    : defaults.keywords.concat([])

  const author = pageData.author ?? defaults.author
  const creator = defaults.creator
  const description = pageData.description ?? defaults.description

  const ogTitle = pageData.og_title || pageData.title  
  const finalOgTitle = ogTitle && ogTitle !== defaults.title
    ? `${defaults.title} ${ogTitle}`
    : defaults.title
  const ogDescription = pageData.og_description ?? description
  const ogImage = pageData.og_image ?? pageData.image ?? defaults.image2x1
  const ogImageAlt = pageData.og_image_alt ?? pageData.image_alt ?? defaults.imageAlt
  const ogUrl = defaults.rootUrl + (pageData.path ?? routePath)

  const xTitle = pageData.x_title || pageData.title
  const finalXTitle = xTitle && xTitle !== defaults.title
    ? `${defaults.title} ${xTitle}`
    : defaults.title
  const xDescription = pageData.x_description ?? description
  const xImage = pageData.x_image || pageData.image || defaults.image2x1
  const xImageAlt = pageData.x_image_alt ?? pageData.image_alt ?? defaults.imageAlt
  const xCard: UseSeoMetaInput['twitterCard'] = pageData.x_card as UseSeoMetaInput['twitterCard'] ?? defaults.twitterCard
  const xCreatorHandle = pageData.x_creator_handle ?? defaults.twitterCreatorHandle

  const headData: UseHeadInput = {
    link: [
      {
        rel: 'canonical',
        href: defaults.rootUrl + routePath,
      },
    ],
  }

  const seoMetaData: UseSeoMetaInput = {
    description,
    author,
    keywords: keywords.join(", ").toLowerCase(),
    creator,
    ogType: defaults.ogType,
    ogTitle: finalOgTitle,
    ogDescription,
    ogImage: defaults.rootUrl + ogImage,
    ogImageAlt,
    ogSiteName: defaults.siteName,
    ogUrl,
    robots: defaults.robots,
    twitterTitle: finalXTitle,
    twitterDescription: xDescription,
    twitterImage: defaults.rootUrl + xImage,
    twitterImageAlt: xImageAlt,
    twitterCard: xCard,
    twitterSite: defaults.twitterSiteHandle,
    twitterCreator: xCreatorHandle,
  }

  return {
    headData,
    seoMetaData,
    pageData,
  }
}
